import {
  isActivityLogPostBlocked,
  markActivityLogRateLimitedFromError,
} from '@/utils/activityLogging/activityLogClientRateLimit';
import { postHubApi } from '@/utils/hooks/api/typescript/methodHelpers';

/**
 * Client activity log: allowlisted template id + params only (server renders stored text).
 * Extra keys (e.g. Workflow `acctName` / `tblName`) are forwarded in `activityData`; the API validates per module.
 */
export type ClientActivityLogArgs = {
  status: string;
  messageTemplateId: string;
  messageParams?: Record<string, unknown>;
  feature?: string;
  [extra: string]: unknown;
};

/** Keys handled explicitly above; also drops retired props so they are never forwarded in `extras`. */
const RESERVED_LOG_KEYS = new Set([
  'module',
  'status',
  'feature',
  'async',
  'messageTemplateId',
  'messageParams',
  'systemCreated',
  'message',
]);

export type UseLogResult = {
  log: (args: ClientActivityLogArgs) => void;
  requestStatus: string;
};

export default function useLog(module: string): UseLogResult {
  function log(args: ClientActivityLogArgs): void {
    if (isActivityLogPostBlocked()) {
      return;
    }

    const feature = args.feature ?? '';

    const extras = Object.fromEntries(
      Object.entries(args).filter(
        ([k, v]) => !RESERVED_LOG_KEYS.has(k) && v != null
      )
    );

    const activityData: Record<string, unknown> = {
      module,
      status: args.status,
      feature,
      messageTemplateId: args.messageTemplateId,
      messageParams: args.messageParams ?? {},
      ...extras,
    };

    // Best-effort telemetry: rate limits and network errors must not affect page UX.
    void postHubApi('/activities', { activityData }).catch((error) => {
      markActivityLogRateLimitedFromError(error);
    });
  }

  return {
    log,
    requestStatus: 'idle',
  };
}
